"use client";

import {
  BarChart3,
  Bell,
  Building2,
  Cpu,
  CreditCard,
  Database,
  FileBarChart,
  HeartPulse,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Lock,
  Radio,
  Settings,
  ShieldCheck,
  Table2,
  Tags,
  ReceiptText,
  UploadCloud,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import { hasModulePermission, type ModulePermissionMap } from "@/modules/auth/access";
import { canManageTenantUsers, isAdmin, isSuperAdmin } from "@/modules/auth/permissions";
import type { AuthUser } from "@/modules/auth/types";

export type NavGroupId = "operations" | "insights" | "workflows" | "administration" | "system";
export type SidebarNavItem = {
  href: string;
  moduleKey: string;
  moduleKeys?: string[];
  label: string;
  short: string;
  icon: LucideIcon;
  children?: SidebarNavItem[];
  adminOnly?: boolean;
  userManagersOnly?: boolean;
  superAdminOnly?: boolean;
  customerVisible?: boolean;
};
export type SidebarNavGroup = { id: NavGroupId; label: string; items: SidebarNavItem[] };

export const NO_ACCESS_ROUTE = "/access-pending";

const reportChildren: SidebarNavItem[] = [
  { href: "/reports", moduleKey: "reports", label: "Report delivery", short: "RD", icon: FileBarChart },
  { href: "/reports/channels", moduleKey: "reports.channels", label: "Channels", short: "CR", icon: FileBarChart },
  { href: "/reports/users", moduleKey: "reports.users", label: "Users", short: "UR", icon: Users },
  { href: "/reports/sales/time", moduleKey: "reports.sales.time", label: "Sales by time", short: "TS", icon: FileBarChart },
  { href: "/reports/sales/region", moduleKey: "reports.sales.region", label: "Sales by region", short: "RS", icon: FileBarChart },
  { href: "/reports/dataloggers/warranty", moduleKey: "reports.dataloggers.warranty", label: "Logger warranty", short: "WR", icon: FileBarChart },
  { href: "/reports/alerts", moduleKey: "reports.alerts", label: "Alerts", short: "AR", icon: FileBarChart },
  { href: "/reports/support/tickets", moduleKey: "reports.support.tickets", label: "Support tickets", short: "SR", icon: FileBarChart },
  { href: "/reports/data/usage", moduleKey: "reports.data.usage", label: "Data usage", short: "DU", icon: FileBarChart },
  { href: "/reports/expenses", moduleKey: "expenses", label: "Expenses", short: "EP", icon: ReceiptText },
];

export const SIDEBAR_GROUPS: SidebarNavGroup[] = [
  {
    id: "operations",
    label: "Operations",
    items: [
      { href: "/overview", moduleKey: "overview", label: "Overview", short: "OV", icon: LayoutDashboard },
      { href: "/devices", moduleKey: "devices", label: "Data loggers", short: "DL", icon: Cpu },
      { href: "/devices/master-view", moduleKey: "master_view", label: "Master view", short: "MV", icon: Table2 },
      { href: "/live", moduleKey: "live", label: "Live monitoring", short: "LV", icon: Radio },
    ],
  },
  {
    id: "insights",
    label: "Analysis",
    items: [
      { href: "/explorer", moduleKey: "explorer", label: "Explorer", short: "EX", icon: Table2 },
      { href: "/analytics", moduleKey: "analytics", label: "Analytics", short: "AN", icon: BarChart3 },
      { href: "/dashboards", moduleKey: "dashboards", label: "Dashboards", short: "DB", icon: LayoutGrid, userManagersOnly: true },
      {
        href: "/reports/users",
        moduleKey: "reports",
        moduleKeys: reportChildren.map((item) => item.moduleKey),
        label: "Reports",
        short: "RP",
        icon: FileBarChart,
        children: reportChildren,
      },
    ],
  },
  {
    id: "workflows",
    label: "Workflows",
    items: [
      { href: "/alerts", moduleKey: "automations", label: "Alerts", short: "AL", icon: Workflow },
      { href: "/ota", moduleKey: "ota", label: "OTA updates", short: "OTA", icon: UploadCloud },
      { href: "/notifications", moduleKey: "alerts", label: "Notifications", short: "NT", icon: Bell },
      { href: "/announcements", moduleKey: "announcements", label: "Announcements", short: "AN", icon: Bell, customerVisible: true },
    ],
  },
  {
    id: "administration",
    label: "Administration",
    items: [
      { href: "/users", moduleKey: "users", label: "Users", short: "US", icon: Users, userManagersOnly: true },
      { href: "/platform-users", moduleKey: "platform_users", label: "Platform users", short: "PU", icon: Users },
      { href: "/platform-roles", moduleKey: "platform_roles", label: "Roles", short: "PR", icon: ShieldCheck, superAdminOnly: true },
      { href: "/device-categories", moduleKey: "device_categories", label: "Device categories", short: "DC", icon: Tags },
      { href: "/expense-categories", moduleKey: "expense_categories", label: "Expense categories", short: "EC", icon: ReceiptText },
      { href: "/tenants", moduleKey: "tenants", label: "Workspaces", short: "WS", icon: Building2, superAdminOnly: true },
      { href: "/permissions", moduleKey: "permissions", moduleKeys: ["permissions", "roles"], label: "Permissions", short: "PM", icon: ShieldCheck },
      { href: "/settings", moduleKey: "settings", moduleKeys: ["entitlements", "activity_policy"], label: "Settings", short: "SE", icon: Settings },
      { href: "/organizations", moduleKey: "locations", label: "Organizations", short: "OR", icon: Building2 },
      { href: "/billing", moduleKey: "billing", label: "Billing", short: "BI", icon: CreditCard },
      { href: "/support", moduleKey: "support", label: "Support", short: "SU", icon: LifeBuoy },
      { href: "/data-management", moduleKey: "data_management", label: "Data management", short: "DM", icon: Database },
    ],
  },
  {
    id: "system",
    label: "System",
    items: [
      { href: "/security", moduleKey: "security", label: "Security", short: "SC", icon: Lock },
      { href: "/system-health", moduleKey: "system_health", label: "System health", short: "HE", icon: HeartPulse },
    ],
  },
];

export function filterSidebarGroupsForUser(user: AuthUser | null, permissionMap?: ModulePermissionMap | null): SidebarNavGroup[] {
  const superAdmin = isSuperAdmin(user);
  const platformActor = superAdmin || user?.accountType === "platform";
  const userManager = canManageTenantUsers(user);
  const admin = isAdmin(user);
  // const hiddenForSuperAdmin = new Set(["/live", "/explorer", "/analytics", "/dashboards", "/alerts", "/notifications"]);
  const hiddenForSuperAdmin = new Set(["/dashboards", "/alerts", "/notifications"]);

  return SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .map((item) => {
        const children = item.children?.filter((child) => hasModulePermission(user, permissionMap, child.moduleKey, "view"));
        const next = children ? { ...item, href: children[0]?.href ?? item.href, children } : item;
        if (!superAdmin && next.href === "/permissions") return { ...next, label: "Roles", short: "RO", moduleKey: "roles", moduleKeys: ["roles"] };
        if (platformActor && next.href === "/users") return { ...next, label: "Customers", short: "CU" };
        if (platformActor && next.href === "/platform-users") return { ...next, label: "Users", short: "US" };
        return next;
      })
      .filter((item) => {
        if (item.superAdminOnly && !superAdmin) return false;
        if (item.adminOnly && !admin) return false;
        if (item.userManagersOnly && !userManager) return false;
        if (platformActor && item.href === "/dashboards") return false;
        if (superAdmin && hiddenForSuperAdmin.has(item.href)) return false;
        if (item.customerVisible && user?.accountType !== "platform" && !superAdmin) return true;
        const permissionKeys = item.moduleKeys ?? [item.moduleKey];
        return Boolean(item.children?.length) || permissionKeys.some((key) => hasModulePermission(user, permissionMap, key, "view"));
      }),
  })).filter((group) => group.items.length > 0);
}

export function navItemActive(currentPath: string, href: string): boolean {
  const [pathname = currentPath, search = ""] = currentPath.split("?");
  const [hrefPath = href, hrefSearch = ""] = href.split("?");
  if (hrefSearch) {
    const currentParams = new URLSearchParams(search);
    const hrefParams = new URLSearchParams(hrefSearch);
    const dashboardId = hrefParams.get("dashboard");
    return hrefPath === pathname && Boolean(dashboardId) && currentParams.get("dashboard") === dashboardId;
  }
  if (href === "/overview") return pathname === "/overview" && !new URLSearchParams(search).has("dashboard");
  // Report delivery is the parent route for report children, not an ancestor page.
  // Treat it as exact so opening a child report does not highlight it as selected.
  if (hrefPath === "/reports") return pathname === "/reports";
  if (href === "/fleet") return pathname === "/fleet" || pathname.startsWith("/fleet/") || pathname === "/dashboard" || pathname.startsWith("/dashboard/");
  if (hrefPath.startsWith("/reports/")) return pathname === hrefPath;
  if (hrefPath === "/devices") return pathname === "/devices" || pathname.startsWith("/devices/") && !pathname.startsWith("/devices/master-view");
  return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
}

export function navItemOrChildActive(currentPath: string, item: SidebarNavItem): boolean {
  const pathname = currentPath.split("?")[0] ?? "";
  if (item.children?.length && item.children.some((child) => child.href.startsWith("/reports/"))) {
    return pathname === "/reports" || pathname.startsWith("/reports/");
  }
  return navItemActive(currentPath, item.href) || Boolean(item.children?.some((child) => navItemActive(currentPath, child.href)));
}

export type CommandPaletteEntry = { id: string; label: string; href: string; group: string };
export function flattenNavForPalette(groups: SidebarNavGroup[]): CommandPaletteEntry[] {
  return groups.flatMap((group) => group.items.flatMap((item) => [
    { id: `${group.id}-${item.href}`, label: item.label, href: item.href, group: group.label },
    ...(item.children ?? []).map((child) => ({ id: `${group.id}-${child.href}`, label: child.label, href: child.href, group: item.label })),
  ]));
}

export function resolveAccessibleRoute(user: AuthUser | null, permissionMap?: ModulePermissionMap | null, preferredPath?: string | null): string {
  const items = filterSidebarGroupsForUser(user, permissionMap).flatMap((group) => group.items.flatMap((item) => [item, ...(item.children ?? [])]));
  if (preferredPath && items.some((item) => navItemActive(preferredPath, item.href))) return preferredPath;
  return items[0]?.href ?? (user ? NO_ACCESS_ROUTE : "/");
}
